import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Globe, ShoppingCart, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import FormSuccess from '../../components/FormSuccess';

const packages = [
  {
    id: 'wordpress-express',
    name: 'WordPress Express',
    price: '99.950 kr.',
    description: 'Staðlað útlit hannað af Vefsíðu',
    features: [
      'Staðlað WordPress þema',
      'Grunnstilling og uppsetning',
      'Tengingar við samfélagsmiðla',
      'SSL vottun',
      '5 síður innifaldar'
    ]
  },
  {
    id: 'woocommerce-express',
    name: 'WooCommerce Express',
    price: '149.950 kr.',
    description: 'Staðlað netverslunarútlit',
    features: [
      'Staðlað WooCommerce þema',
      'Grunnstilling og uppsetning',
      'Greiðslugáttir',
      'Vörustjórnun',
      'SSL vottun',
      '10 síður innifaldar'
    ]
  }
];

const WordPressExpressOrder = () => {
  const [selectedPackage, setSelectedPackage] = useState<string>('wordpress-express');
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    domain: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    
    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': 'wordpress-express-order',
          ...formData,
          selected_package: selectedPackage
        }).toString()
      });
      
      if (response.ok) {
        setShowSuccess(true);
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          domain: '',
          message: ''
        });
        form.reset();
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Villa kom upp við að senda form. Vinsamlegast reyndu aftur.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Panta WordPress Express
          </h1>
          <p className="text-xl text-gray-600">
            Veldu pakka og fylltu út upplýsingar til að panta þína WordPress Express lausn. Eftir að pöntun er móttekin höfum við samband og köllum eftir öllum upplýsingum og efni sem okkur vantar.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-12">
          <motion.div 
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl shadow-sm p-8">
              <form 
                name="wordpress-express-order"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <input type="hidden" name="form-name" value="wordpress-express-order" />
                <p hidden>
                  <label>
                    Don't fill this out: <input name="bot-field" />
                  </label>
                </p>
                <input 
                  type="hidden" 
                  name="selected_package" 
                  value={selectedPackage} 
                />

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Veldu pakka</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {packages.map((pkg) => (
                      <motion.div
                        key={pkg.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                          selectedPackage === pkg.id
                            ? 'border-[#EA295A] bg-[#EA295A]/5'
                            : 'border-gray-200 hover:border-[#EA295A]/50'
                        }`}
                        onClick={() => setSelectedPackage(pkg.id)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            {pkg.id === 'wordpress-express' ? (
                              <Globe className={`w-6 h-6 ${
                                selectedPackage === pkg.id
                                  ? 'text-[#EA295A]'
                                  : 'text-gray-400'
                              }`} />
                            ) : (
                              <ShoppingCart className={`w-6 h-6 ${
                                selectedPackage === pkg.id
                                  ? 'text-[#EA295A]'
                                  : 'text-gray-400'
                              }`} />
                            )}
                          </div>
                          <div className="ml-3">
                            <h4 className="text-sm font-medium text-gray-900">{pkg.name}</h4>
                            <p className="mt-1 text-sm font-bold text-[#EA295A]">{pkg.price}</p>
                          </div>
                          {selectedPackage === pkg.id && (
                            <CheckCircle2 className="ml-auto w-5 h-5 text-[#EA295A]" />
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Upplýsingar</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Nafn
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Netfang
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Sími
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                        Fyrirtæki
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="domain" className="block text-sm font-medium text-gray-700 mb-1">
                    Lén (ef þú átt lén)
                  </label>
                  <input
                    type="text"
                    id="domain"
                    name="domain"
                    value={formData.domain}
                    onChange={handleInputChange}
                    placeholder="t.d. example.is"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Viðbótarupplýsingar
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                    placeholder="Segðu okkur meira um þínar þarfir..."
                  />
                </div>

                <motion.button
                  type="submit"
                  className="w-full bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  PANTA NÚNA
                </motion.button>
              </form>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="bg-white rounded-2xl shadow-sm p-8 sticky top-32">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Innifalið í pakkanum</h3>
              <div className="space-y-6">
                {packages.find(pkg => pkg.id === selectedPackage)?.features.map((feature, index) => (
                  <motion.div 
                    key={index}
                    className="flex items-start"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-3" />
                    <span className="text-gray-600">{feature}</span>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 pt-8 border-t border-gray-100">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Verð:</span>
                  <div>
                    <span className="text-2xl font-bold text-[#EA295A]">
                      {packages.find(pkg => pkg.id === selectedPackage)?.price}
                    </span>
                    <p className="text-gray-500 text-sm text-right">verð án VSK</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {showSuccess && (
        <FormSuccess 
          message="Takk fyrir pöntunina! Við munum hafa samband við þig eins fljótt og auðið er til að hefja verkefnið."
          onClose={() => setShowSuccess(false)}
        />
      )}
    </div>
  );
};

export default WordPressExpressOrder;